import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mic, MicOff, PhoneOff, RotateCcw, Send, AudioLines, Loader2, WifiOff, AlertTriangle } from 'lucide-react';
import { LiveSession, type LiveSnapshot, type LiveState } from '../voice/live';
import { fmt } from '../api';
import { Chip, Note } from './ui';

const LABEL: Record<LiveState, string> = {
  idle: 'Ready when you are',
  requesting_permission: 'Allow the microphone',
  connecting: 'Connecting…',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking',
  reconnecting: 'Reconnecting…',
  error: 'Something went wrong',
  ending: 'Ending…',
  ended: 'Conversation ended',
};

const HINT: Partial<Record<LiveState, string>> = {
  idle: 'Press start, then just talk. English, Hindi or Hinglish. No buttons between turns.',
  requesting_permission: 'Your browser will ask for microphone access.',
  connecting: 'Opening a live line to the agent.',
  listening: 'Go ahead. Pause when you are done and it will answer.',
  thinking: 'Working on a reply. Start talking to interrupt.',
  speaking: 'Talk over it any time to interrupt.',
  reconnecting: 'Hold on, getting the connection back.',
};

export default function TalkCard({
  agentId,
  agentName,
  maxSeconds,
  socketPath,
  transcriptLink = true,
  diagnostics = true,
  onEnded,
}: {
  agentId: string;
  agentName: string;
  maxSeconds: number | null;
  /** A demo link talks on its own socket. */
  socketPath?: string;
  /** Signed-in accounts can open the full transcript; a demo link cannot. */
  transcriptLink?: boolean;
  /** Latency and turn numbers — for the owner and teammates, not for a prospect. */
  diagnostics?: boolean;
  onEnded?: () => void;
}) {
  const session = useMemo(() => new LiveSession(socketPath), [socketPath]);
  const [s, setS] = useState<LiveSnapshot | null>(null);
  const [typed, setTyped] = useState('');
  const [nowTs, setNowTs] = useState(Date.now());
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => session.subscribe(setS), [session]);

  // Leaving the page, closing the tab, or unmounting always releases the mic.
  useEffect(() => {
    const hide = () => session.dispose();
    window.addEventListener('pagehide', hide);
    (window as any).__liveSession = session; // for automated browser checks
    return () => {
      window.removeEventListener('pagehide', hide);
      session.dispose();
    };
  }, [session]);

  useEffect(() => {
    if (!s?.startedAt || !session.isActive) return;
    const id = window.setInterval(() => setNowTs(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [s?.startedAt, s?.state, session]);

  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [s?.transcript.length, s?.partial]);

  // Tell the page when a conversation is over (a demo link refreshes its time left).
  useEffect(() => {
    if (s?.state === 'ended') onEnded?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.state]);

  // Switching agent mid-conversation ends the current one first.
  const lastAgent = useRef(agentId);
  useEffect(() => {
    if (lastAgent.current !== agentId && session.isActive) session.end();
    lastAgent.current = agentId;
  }, [agentId, session]);

  if (!s) return null;
  const active = session.isActive;
  const level = s.state === 'speaking' ? s.outLevel : s.state === 'listening' ? s.micLevel : 0;
  const elapsed = s.startedAt && active ? Math.floor((nowTs - s.startedAt) / 1000) : 0;
  const caption = s.state === 'speaking' || s.state === 'thinking' ? (s.agentSaying ? { who: agentName, text: s.agentSaying } : null) : s.partial ? { who: 'You', text: s.partial } : null;

  function sendTyped(e: FormEvent) {
    e.preventDefault();
    session.sendText(typed);
    setTyped('');
  }

  return (
    <section className="card live" aria-labelledby="talk-title">
      <div className="card-head">
        <div>
          <h2 id="talk-title">Talk in your browser</h2>
          <p>A live, hands-free conversation. Interrupt it like you would a person.</p>
        </div>
        {active && s.startedAt ? (
          <span className="timer" aria-label="Elapsed time">
            {fmt.duration(elapsed * 1000)}
            {maxSeconds ? ` / ${fmt.duration(maxSeconds * 1000)}` : ''}
          </span>
        ) : null}
      </div>

      <div className="stage">
        <div className="orb" data-state={s.state} style={{ ['--level' as any]: level.toFixed(3) }} aria-hidden="true">
          {s.state === 'error' ? <AlertTriangle /> : s.state === 'reconnecting' ? <WifiOff /> : s.state === 'connecting' || s.state === 'requesting_permission' || s.state === 'ending' ? <Loader2 className="spin-icon" /> : s.state === 'speaking' ? <AudioLines /> : s.state === 'idle' || s.state === 'ended' ? <MicOff /> : <Mic />}
        </div>
        <div className="state-label" role="status" aria-live="polite" data-testid="live-state">{LABEL[s.state]}</div>
        <div className="state-hint">{s.notice ?? HINT[s.state] ?? ''}</div>
        <div className={`caption ${caption ? '' : 'empty-cap'}`} aria-live="off" data-testid="caption">
          {caption ? (
            <>
              <span className="who">{caption.who}</span>
              {caption.text}
            </>
          ) : active ? (
            'Captions appear here as you speak.'
          ) : (
            'Live captions will appear here.'
          )}
        </div>

        <div className="row" style={{ justifyContent: 'center' }}>
          {!active && s.state !== 'ending' ? (
            <button className="btn primary lg" onClick={() => session.start(agentId)} disabled={!agentId} data-testid="start">
              {s.state === 'error' || s.state === 'ended' ? <RotateCcw aria-hidden="true" /> : <Mic aria-hidden="true" />}
              {s.state === 'error' ? 'Try again' : s.state === 'ended' ? 'Start a new conversation' : 'Start live conversation'}
            </button>
          ) : (
            <button className="btn danger solid lg" onClick={() => session.end()} disabled={s.state === 'ending'} data-testid="end">
              <PhoneOff aria-hidden="true" /> End conversation
            </button>
          )}
        </div>
      </div>

      {s.error ? (
        <Note tone="error">
          {s.error.message}
          {s.error.code === 'permission_denied' ? (
            <span className="small" style={{ display: 'block', marginTop: 4 }}>
              Tip: click the lock or microphone icon in the address bar, set Microphone to Allow, then try again.
            </span>
          ) : null}
        </Note>
      ) : null}
      {s.state === 'ended' && s.conversationId ? (
        <Note tone="success">
          {s.notice ?? 'Conversation ended.'}{' '}
          {transcriptLink ? <Link to={`/conversations/${s.conversationId}`}>See the full transcript</Link> : null}
        </Note>
      ) : null}

      <div>
        <div className="row between" style={{ marginBottom: 6 }}>
          <span className="eyebrow">Transcript</span>
          {s.diag.turns ? <Chip tone="neutral">{s.diag.turns} turns</Chip> : null}
        </div>
        <div className="transcript" ref={transcriptRef} data-testid="transcript" aria-live="polite">
          {s.transcript.length === 0 ? <p className="small muted" style={{ margin: 0 }}>Nothing yet. The agent will greet you first.</p> : null}
          {s.transcript.map((t) => (
            <div key={t.id} className={`bubble ${t.role} ${t.interrupted ? 'cut' : ''}`}>
              <span className="meta">{t.role === 'user' ? 'You' : agentName}{t.interrupted ? ' · interrupted' : ''}</span>
              {t.text}
            </div>
          ))}
        </div>
      </div>

      <details className="diag">
        <summary>
          <span>Type instead</span>
          <span className="muted small">accessibility fallback</span>
        </summary>
        <div className="diag-body">
          <form className="row" onSubmit={sendTyped}>
            <label htmlFor="typed" className="sr-only">Message</label>
            <input id="typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={active ? 'Type a message and press Enter' : 'Start a conversation first'} disabled={!active} style={{ flex: 1, minWidth: 180 }} />
            <button className="btn" disabled={!active || !typed.trim()}>
              <Send aria-hidden="true" /> Send
            </button>
          </form>
        </div>
      </details>

      {diagnostics ? (
      <details className="diag" data-testid="diagnostics">
        <summary>
          <span>Diagnostics</span>
          <span className="muted small">{s.diag.firstAudioHeardMs !== null ? `reply heard ${s.diag.firstAudioHeardMs} ms after your turn was understood` : 'latency, turns, barge-in'}</span>
        </summary>
        <div className="diag-body">
          <table>
            <tbody>
              <tr><td>Connect (start → ready)</td><td className="num" data-metric="connect">{fmt.ms(s.diag.connectMs)}</td></tr>
              <tr><td>First caption after speech starts</td><td className="num" data-metric="first_partial">{fmt.ms(s.diag.firstPartialMs)}</td></tr>
              <tr><td>End of your turn understood (after you stop)</td><td className="num" data-metric="turn_final">{fmt.ms(s.diag.turnFinalMs)}</td></tr>
              <tr><td>First reply token (brain)</td><td className="num" data-metric="first_token">{fmt.ms(s.diag.firstTokenMs)}</td></tr>
              <tr><td>First reply audio (server)</td><td className="num" data-metric="first_audio">{fmt.ms(s.diag.firstAudioMs)}</td></tr>
              <tr><td>First reply audio heard (after your turn was understood)</td><td className="num" data-metric="first_audio_heard">{fmt.ms(s.diag.firstAudioHeardMs)}</td></tr>
              <tr><td>Barge-in: speech → detected</td><td className="num" data-metric="barge_detect">{fmt.ms(s.diag.bargeInDetectMs)}</td></tr>
              <tr><td>Barge-in: playback stop</td><td className="num" data-metric="barge_stop">{fmt.ms(s.diag.bargeInStopMs)}</td></tr>
              <tr><td>Turns · interruptions · reconnects</td><td className="num" data-metric="counts">{s.diag.turns} · {s.diag.bargeIns} · {s.diag.reconnects}</td></tr>
            </tbody>
          </table>
        </div>
      </details>
      ) : null}
    </section>
  );
}
